import { type NonEmptyArray } from '@/types';

export type TimeSpent = {
  /**
   * The number of hours spent on a task. Derived from totalTime.
   */
  readonly hours: number;
  /**
   * The number of minutes spent on a task. Derived from totalTime.
   */
  readonly minutes: number;
};

export type PartialTimeSpent =
  | { readonly hours: number; readonly minutes?: never }
  | { readonly hours?: never; readonly minutes: number }
  | TimeSpent;

export const timeSpentToMinutes = (timeSpent: PartialTimeSpent): number =>
  (timeSpent.hours ?? 0) * 60 + (timeSpent.minutes ?? 0);

/**
 * Creates a {@link TimeSpent} object from the provided total number of minutes.
 *
 * @param {number} minutes The total number of minutes spent on a task.
 *
 * @returns {TimeSpent}
 *   An object containing the number of hours, minutes, and total time spent on a task.
 */
export const parseTimeSpent = (value: number | PartialTimeSpent): TimeSpent => {
  if (typeof value === 'number') {
    return {
      hours: Math.floor(value / 60),
      minutes: value % 60,
    };
  }
  return parseTimeSpent(timeSpentToMinutes(value));
};

const TimeUnits = ['hours', 'minutes'] as const;
type TimeUnit = (typeof TimeUnits)[number];

export interface FormatTimeSpentOptions {
  /**
   * Defines whether or not the 'minutes' and/or 'hours' constituents should be excluded from the
   * resulting formatted string if they are zero.  By default, neither constituent is excluded.
   *
   * If provided as 'true', both constituents will be excluded if they are zero.  Otherwise, the
   * specific constituents to exclude can be defined in an array, e.g., `['hours']` to exclude just
   * the 'hours' constituent if it is zero.
   */
  readonly excludeIfZero?: NonEmptyArray<TimeUnit> | true;
  /**
   * Defines whether or not just the 'hours' constituent or just the 'minutes' constituent should
   * be included.  By default, both 'hours' and 'minutes' are included.  If only the 'minutes'
   * constituent is included, and the total time spent is greater than or equal to 60 minutes, the
   * 'minutes' constituent will represent the total number of minutes, not just the leftover
   * minutes after hours have been calculated.
   */
  readonly include?: TimeUnit;
  readonly includeCommas?: boolean;
}

export const formatTimeSpent = (
  value: number | PartialTimeSpent,
  options?: FormatTimeSpentOptions,
) => {
  const timeSpent = parseTimeSpent(value);

  const isIncluded = (unit: TimeUnit): boolean => {
    const include = options?.include ?? TimeUnits;
    if (Array.isArray(include)) {
      return include.includes(unit);
    }
    return include === unit;
  };

  const isExcluded = (unit: TimeUnit): boolean => {
    const excludeIfZero = options?.excludeIfZero ?? false;
    return (
      timeSpent[unit] === 0 &&
      (excludeIfZero === true || (Array.isArray(excludeIfZero) && excludeIfZero.includes(unit)))
    );
  };

  const formatUnit = (unit: TimeUnit): null | string => {
    if (!isIncluded(unit) || isExcluded(unit)) {
      return null;
    }
    let unitValue = timeSpent[unit];
    if (unit === 'minutes' && !isIncluded('hours')) {
      unitValue = timeSpentToMinutes(timeSpent);
    }
    if (options?.includeCommas) {
      return unitValue.toLocaleString('en-US');
    }
    return unitValue.toString();
  };

  const hours = formatUnit('hours');
  const minutes = formatUnit('minutes');

  if (hours && minutes) {
    return `${hours}h ${minutes}m`;
  } else if (hours) {
    return `${hours}h`;
  } else if (minutes) {
    return `${minutes}m`;
  }
  return '';
};

export const isTimeSpent = (value: unknown): value is TimeSpent =>
  typeof value === 'object' &&
  value !== null &&
  'hours' in value &&
  'minutes' in value &&
  typeof value.hours === 'number' &&
  typeof value.minutes === 'number';
